import { AI_GENERATORS, PHONE_BRANDS, CAMERA_BRANDS, AI_RESOLUTION_SIGNATURES } from './constants';

export const classifySource = (file, tags) => {
  let scores = { dslr: 0, phone: 0, ai: 0, unknown: 0 };
  const logicChain = [];
  const conflicts = [];

  const width = tags['Image Width']?.value || tags['ExifImageWidth']?.value;
  const height = tags['Image Height']?.value || tags['ExifImageHeight']?.value;
  const resKey = width && height ? `${width}x${height}` : null;

  const rawTags = {
    make: tags['Make']?.description?.trim(),
    model: tags['Model']?.description?.trim(),
    software: tags['Software']?.description || tags['CreatorTool']?.description,
    focal: tags['FocalLength']?.value ? (tags['FocalLength']?.value[0] / tags['FocalLength']?.value[1]) : null,
    focal35: tags['FocalLengthIn35mmFormat']?.value,
    colorSpace: tags['ColorSpace']?.description,
    compression: tags['Compression']?.description
  };

  const sw = rawTags.software?.toLowerCase() || '';

  // 1. NEURAL / AI LOGIC (High Weight)
  const aiMatch = AI_GENERATORS.find(g => sw.includes(g.toLowerCase()));
  if (aiMatch) {
    scores.ai += 120; // Immediate trigger
    logicChain.push({ marker: 'Generator Sig', value: aiMatch, reason: 'Explicit software attribution to generative engine.' });
  }

  if (AI_RESOLUTION_SIGNATURES.includes(resKey)) {
    scores.ai += 40;
    logicChain.push({ marker: 'Neural Res', value: resKey, reason: 'Matches standardized latent diffusion training dimensions.' });
  }

  // 2. OPTICAL / DSLR LOGIC
  if (rawTags.make && CAMERA_BRANDS.some(b => rawTags.make.includes(b))) {
    scores.dslr += 60;
    logicChain.push({ marker: 'Optics Brand', value: rawTags.make, reason: 'Manufacturer signatures consistent with discrete sensors.' });
  }

  // 3. MOBILE / PHONE LOGIC
  if (rawTags.make && PHONE_BRANDS.some(b => rawTags.make.includes(b))) {
    scores.phone += 60;
    logicChain.push({ marker: 'Mobile Device', value: rawTags.make, reason: 'Hardware ID matches mobile communication platform.' });
  }
  
  if (rawTags.focal && rawTags.focal < 8 && rawTags.focal35 > 13) {
    scores.phone += 30;
    logicChain.push({ marker: 'Focal Physics', value: `${rawTags.focal.toFixed(1)}mm`, reason: 'Optical physics match sub-1/2" smartphone sensor geometry.' });
  }

  // 4. NEUTRAL SIGNATURES (Prevents "Everything is AI" bias)
  if (rawTags.colorSpace === 'sRGB' || rawTags.compression === 'JPEG (old-style)') {
    scores.unknown += 20; // Adds to the "Human/Neutral" bucket
    logicChain.push({ marker: 'Encoding', value: rawTags.colorSpace || 'Standard', reason: 'Standard digital encoding patterns detected.' });
  }

  // Final Decision Logic
  const total = (scores.ai + scores.dslr + scores.phone + scores.unknown) || 1;
  const normalized = {
    ai: Math.round((scores.ai / total) * 100),
    dslr: Math.round((scores.dslr / total) * 100),
    phone: Math.round((scores.phone / total) * 100),
    unknown: Math.round((scores.unknown / total) * 100)
  };

  let sourceType = "Digital Export";
  let confidence = normalized.unknown || 50;

  if (normalized.ai > 45) { 
    sourceType = "AI Generated"; 
    confidence = normalized.ai; 
  } else if (normalized.dslr > 45) { 
    sourceType = "DSLR / Mirrorless"; 
    confidence = normalized.dslr; 
  } else if (normalized.phone > 45) { 
    sourceType = "Camera Phone"; 
    confidence = normalized.phone; 
  }

  return { sourceType, confidence, logicChain, conflicts, normalized };
};